import React, { FC, useState } from 'react'

import moment from 'moment/moment'
import { Loader2Icon } from 'lucide-react'

import { Button } from '../../../../components/ui/button'

import {
  IPaymentStatus,
  IPaymentType,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
} from '../../../../types/payment'

interface StatusBadgeProps {
  isAdjusted: boolean
  isScratched: boolean
  scratchedDate: Date | null
  isRefunded: boolean
  paymentStatus: IPaymentStatus
  refundedDate: Date | null
  paidDate: Date | string | null
  paymentType: IPaymentType
  ownerId: string | null
  ownerName: string | null
  recipientId: string | null
  recipientName: string | null
  userId?: string
  onRecipientClick?: (value: string | null) => void
  onUndoMarkMaid?: () => Promise<void>
}

export const PaymentStatus: FC<StatusBadgeProps> = ({
  isAdjusted,
  paymentStatus,
  isScratched,
  scratchedDate,
  isRefunded,
  refundedDate,
  paidDate,
  paymentType,
  ownerId,
  ownerName,
  recipientId,
  recipientName,
  userId,
  onRecipientClick,
  onUndoMarkMaid,
}) => {
  const [undoLoading, setUndoLoading] = useState(false)

  const adjustedBeforeText = isAdjusted && 'Adjusted, '
  const adjustedAfterText = isAdjusted && ', Adjusted'
  const paymentTypeText = <span className="capitalize">{paymentType?.toLowerCase()}</span>
  const paidByText = ownerId !== recipientId && (
    <>
      {userId !== ownerId && ownerName ? (
        <>
          from{' '}
          <Button
            variant="link"
            className="p-0  text-inherit underline hover:no-underline hover:opacity-70 transition-all"
            onClick={() => onRecipientClick && onRecipientClick(ownerId)}
          >
            {ownerName}
          </Button>
          ,
        </>
      ) : recipientName ? (
        <>
          {paymentStatus === PAYMENT_STATUS.PAID ? 'Paid by ' : 'Sent to '}
          <Button
            variant="link"
            className="p-0  text-inherit underline hover:no-underline hover:opacity-70 transition-all"
            onClick={() => onRecipientClick && onRecipientClick(recipientId)}
          >
            {recipientName}
          </Button>
          ,
        </>
      ) : (
        paymentStatus === PAYMENT_STATUS.PAID && 'Paid,'
      )}
    </>
  )

  if (isRefunded) {
    return (
      <div className="text-xs">
        {adjustedBeforeText}Paid, Refunded {refundedDate && moment(new Date()).format('MMM D')}
      </div>
    )
  }

  if (isScratched && paymentStatus === PAYMENT_STATUS.PAID && paymentType === PAYMENT_TYPE.CARD) {
    return (
      <div className="text-xs">
        {paidByText} {adjustedBeforeText} Refund Pending
      </div>
    )
  }

  if (isScratched && paymentStatus === PAYMENT_STATUS.PAID && paymentType !== PAYMENT_TYPE.CARD) {
    return (
      <div className="text-xs">
        {adjustedBeforeText}Scratched {scratchedDate && ` ${moment(new Date()).format('MMM D')}`},{' '}
        {paymentTypeText}
      </div>
    )
  }

  if (isScratched) {
    return (
      <div className="text-xs">
        {paidByText} {adjustedBeforeText}Scratched
        {scratchedDate && ` ${moment(new Date()).format('MMM D')}`}, {paymentTypeText}
      </div>
    )
  }

  switch (paymentStatus) {
    case PAYMENT_STATUS.PAID:
      return (
        <div className="text-xs flex items-center gap-1">
          {adjustedBeforeText}
          {paidByText} {paidDate && `on ${moment(new Date()).format('MMM D')},`} {paymentTypeText}
          {onUndoMarkMaid &&
            (paymentType === PAYMENT_TYPE.CASH ||
              paymentType === PAYMENT_TYPE.CHECK ||
              paymentType === PAYMENT_TYPE.PAYPAL ||
              paymentType === PAYMENT_TYPE.VENMO) && (
              <>
                •
                <button
                  disabled={undoLoading}
                  type="button"
                  className="underline hover:no-underline inline-flex gap-1 items-center"
                  onClick={async () => {
                    setUndoLoading(true)
                    await onUndoMarkMaid()
                    setUndoLoading(false)
                  }}
                >
                  {undoLoading ? <Loader2Icon className="animate-spin w-3 h-3" /> : 'Undo'}
                </button>
              </>
            )}
        </div>
      )
    case PAYMENT_STATUS.DEFAULT:
      return (
        <div className="text-xs">
          {paidByText} Payment Pending{adjustedAfterText}
        </div>
      )
    default:
      return null
  }
}
